/**
 * UnlockPage.tsx — /unlock, the public page behind every engagement unlock
 * link (`/unlock#t=<token>` — the link staff email and copy into WhatsApp).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement unlock (2026-09-23).
 * Built against the PINNED engagement wire contract (2026-09-23) and the
 * CTO-approved unlock boards (UnlockNeedsPlan / UnlockNeedsForm /
 * UnlockUnlocked / UnlockExpired / UnlockDesktop's left column).
 *
 * WHY IT IS PUBLIC. The link lands in an inbox or a WhatsApp thread and has
 * to work months later on a phone that has never held a session here. The
 * capability is the token, and what it can do is bounded server-side: read
 * what this ONE deal's link offers, and download content already sent to it
 * while — and only while — the server derives the plan unlocked.
 *
 * THE TOKEN COMES OUT OF THE FRAGMENT AND IS SCRUBBED IMMEDIATELY —
 * `#t=<token>`, never a query string, the `lib/hashToken` discipline shared
 * with /preferences, /resubscribe and the pharmacy sign-in. It travels ONLY
 * in request BODIES; the gateway 400s these lanes on any query string.
 * A token that ARRIVES as `?t=` (a rewritten or mangled link) is ignored —
 * never read, never sent — and is ALSO scrubbed from the address bar
 * (`scrubTokenFromQueryString`): ignoring a live capability does not retract
 * it, and un-scrubbed it would park in browser history for the whole visit.
 *
 * ONE DEAD-LINK PAGE FOR EVERY FAILURE SHAPE — deliberately stricter than
 * /preferences, which distinguishes a transport fault. The server answers
 * every bad token with one byte-identical, non-enumerating 404, and this page
 * extends that posture to the whole lane: no token, a 404, a 5xx and a dead
 *
 * network all render the SAME calm UnlockExpired state. A page that offered a
 * retry on some failures and a verdict on others would be an oracle for which
 * kind the server saw, and both onward routes (a call back, a fresh check)
 * are on the dead page anyway.
 *
 * WHAT THE CTA DOES — TWO SHAPES, PER THE SERVER'S RESOLVE CONTRACT.
 * `needs_plan` is the ONE grant-shaped state: `location.assign(nextUrl)` into
 * the LIVE journey — the same `/weightloss#resume=<grant>` lane admins
 * already send, so planViewedAt and the whole checkout behave identically.
 * NOTHING here rebuilds any part of checkout. That grant rides only in the
 * fragment of that URL and is a short-TTL capability minted per resolve; it
 * is never rendered into the DOM (no `<a href>` at rest — the same reasoning
 * as `lib/signedDocument`). `needs_questionnaire` is the OTHER shape: a
 * PLAIN restart URL (`/weightloss?src=unlock` — provenance, not a
 * credential, and no fragment), rendered as a REAL anchor precisely because
 * there is no grant to keep out of the DOM. `lapsed` carries NO nextUrl at
 * all (an ended plan has no lane to hand into), so its onward routes are the
 * page's own — a fresh check and the callback line, never a CTA built out of
 * a null.
 *
 * DOWNLOADS re-ask the server per click (`{token, contentId}` → the ladder):
 * `url` opens the signed read URL through `openSignedDocument`; `locked`
 * RE-RESOLVES the whole page (the server re-derived the state — render what
 * it now says, never argue with a stale screen); `unconfirmed` is an honest
 * inline "can't confirm right now" that stays retryable. A download 404 also
 * re-resolves: either the token has died (→ the dead page, from the resolve's
 * own 404) or the offer list moved on (→ the fresh list).
 *
 * NO PROGRESS BAR ON needs_questionnaire, though the board sketches one: the
 * resolve wire carries NO progress fields, and this portal never manufactures
 * a figure it was not sent (the journey's own progress line follows the same
 * rule — any error may only run one way). The card states what IS true: the
 * answers are saved.
 *
 * ⚠️ THE PHRASE "engagement content" APPEARS NOWHERE PATIENT-FACING — these
 * are "your guides" on every surface a patient reads (the spec sweeps the
 * rendered page for it).
 *
 * NO TRACKING: `/unlock` is in the tracking gate's `RESERVED_SEGMENTS`
 * (tracking/gtm.ts) — a single-segment path is otherwise read as a clinic
 * journey and loads GTM on a page reached from a named patient's own inbox,
 * holding a capability token in its fragment.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { downloadUnlockContent, resolveUnlock } from '../api/engagement';
import type { UnlockItem, UnlockResolveResult } from '../api/types';
import { DEFAULT_CLINIC_SLUG } from '../admin/clinics';
import { CALLBACK_OFFER_LEAD } from '../journey/JourneyCallbackOffer';
import { readHashToken, scrubHashFromAddressBar, scrubTokenFromQueryString } from '../lib/hashToken';
import { openSignedDocument } from '../lib/signedDocument';
import { Button, Card, Help, Microcopy, PageFrame, Spinner, Title, buttonClass, buttonStyle } from '../ui/kit';

/** The browser tab's name while this page is open (WCAG 2.4.2). */
export const UNLOCK_PAGE_TITLE = 'Your guides — The GP Service';

/** The `unconfirmed` outcome — orders-service unreachable, honest and retryable. */
export const UNLOCK_UNCONFIRMED_MESSAGE = "We can't confirm that right now — try again in a moment.";
/** A transport fault on the download itself — nothing about the link, retryable. */
export const UNLOCK_DOWNLOAD_FAILED_MESSAGE = "We couldn't get that guide just now — please try again.";

const CALLBACK_PATH = `/${DEFAULT_CLINIC_SLUG}/callback`;
const JOURNEY_PATH = `/${DEFAULT_CLINIC_SLUG}`;

type UnlockStage =
  | { kind: 'loading' }
  | { kind: 'dead' }
  | { kind: 'ready'; result: UnlockResolveResult };

/** What one tile press resolved to, for the tile's own inline note. */
type TileOutcome = 'opened' | 'unconfirmed' | 'failed' | 'superseded';

export function UnlockPage() {
  // Read the fragment during the FIRST render: the scrub below removes it.
  const [token] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : readHashToken(window.location.hash),
  );
  const [stage, setStage] = useState<UnlockStage>({ kind: 'loading' });
  const [resolveNonce, setResolveNonce] = useState(0);

  // Scrub the live credential out of the address bar the moment we hold it.
  useEffect(() => {
    if (token) scrubHashFromAddressBar();
  }, [token]);

  // A token that arrived as `?t=` is IGNORED (the fragment is the only read
  // lane; the gateway 400s a query-string token) — but ignored is not
  // retracted: a live capability would sit in the address bar and browser
  // history for the whole visit. Scrub it on arrival; it is never SENT.
  useEffect(() => {
    scrubTokenFromQueryString();
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = UNLOCK_PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      // No usable fragment IS a dead link — the page a mail client mangled.
      setStage({ kind: 'dead' });
      return;
    }
    let cancelled = false;
    setStage({ kind: 'loading' });
    resolveUnlock(token).then(
      (result) => {
        if (cancelled) return;
        setStage({ kind: 'ready', result });
      },
      () => {
        // EVERY failure shape lands on the one dead-link page — see the header.
        if (!cancelled) setStage({ kind: 'dead' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token, resolveNonce]);

  /** Re-derive the whole page from the server — the only honest refresh. */
  const reResolve = useCallback(() => {
    setResolveNonce((n) => n + 1);
  }, []);

  const download = useCallback(
    async (contentId: string): Promise<TileOutcome> => {
      if (!token) return 'failed';
      try {
        const result = await downloadUnlockContent(token, contentId);
        if (result.outcome === 'url') {
          openSignedDocument(result.url);
          return 'opened';
        }
        if (result.outcome === 'unconfirmed') return 'unconfirmed';
        // 'locked': the server re-derived the state on THIS call and it is not
        // unlocked any more — re-resolve and render whatever the ladder now
        // says (the locked framing, or the dead page when the token has died).
        reResolve();
        return 'superseded';
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          // Bad token or a guide no longer offered — same re-derive, and the
          // resolve's own 404 is what turns a dead token into the dead page.
          reResolve();
          return 'superseded';
        }
        return 'failed';
      }
    },
    [token, reResolve],
  );

  return (
    <PageFrame narrow>
      {stage.kind === 'loading' ? <Spinner label="Checking your link…" /> : null}
      {stage.kind === 'dead' ? <DeadLinkView /> : null}
      {stage.kind === 'ready' ? <ResolvedView result={stage.result} onDownload={download} /> : null}
    </PageFrame>
  );
}

/** Route the resolved state to its view. An unknown future state falls to the
 * calm nothing-here page: the link is VALID (the resolve answered), so the
 * dead-link page would be false — and the calm page claims nothing. */
function ResolvedView({
  result,
  onDownload,
}: {
  result: UnlockResolveResult;
  onDownload: (contentId: string) => Promise<TileOutcome>;
}) {
  switch (result.state) {
    case 'needs_plan':
      return <NeedsPlanView nextUrl={result.nextUrl} items={result.items} />;
    case 'needs_questionnaire':
      return <NeedsQuestionnaireView nextUrl={result.nextUrl} items={result.items} />;
    case 'waiting':
      return <WaitingView />;
    case 'unlocked':
      return <UnlockedView items={result.items} onDownload={onDownload} />;
    case 'lapsed':
      return <LapsedView items={result.items} />;
    case 'none':
      return <NoneView />;
  }
  return <NoneView />;
}

/* ── The states ─────────────────────────────────────────────────────────── */

function NeedsPlanView({ nextUrl, items }: { nextUrl: string | null; items: UnlockItem[] }) {
  return (
    <div>
      <HeroBadge tone="tint">
        <LockGlyph size={26} />
      </HeroBadge>
      <Title>Your plan is ready to unlock</Title>
      <Help>
        Your clinician has already reviewed your answers and recommended a treatment plan. Choose your plan and add
        your card — about 2 minutes — and your guides unlock straight away.
      </Help>
      {nextUrl ? <HandoffCta nextUrl={nextUrl} label="Choose my plan" /> : null}
      <Microcopy>
        Your card is only pre-authorised — nothing is charged until your clinician gives the final go-ahead.
      </Microcopy>
      <LockedItemsList items={items} />
      <CallbackLine lead={CALLBACK_OFFER_LEAD} />
    </div>
  );
}

function NeedsQuestionnaireView({ nextUrl, items }: { nextUrl: string | null; items: UnlockItem[] }) {
  return (
    <div>
      <HeroBadge tone="tint">
        <TickBoxGlyph size={26} />
      </HeroBadge>
      <Title>You&rsquo;re nearly there — finish your check to unlock</Title>
      <Help>
        You started your health check and your answers are saved. Finish it, and once you&rsquo;re set up your guides
        unlock automatically.
      </Help>
      {/* The board sketches a progress bar here; the wire carries no progress
          figure, so the card states only what is true — see the file header. */}
      <Card style={{ marginBottom: 'var(--gps-s-3)' }}>
        <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>Your check</p>
        <p style={{ margin: '6px 0 0', fontSize: '14px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
          Your answers are saved — pick up right where you left off.
        </p>
      </Card>
      {/* The PLAIN hand-off: this state's nextUrl carries NO grant (see the
          file header), so a real anchor is right — and better than a button:
          it can be opened in a new tab, copied and revisited safely. */}
      {nextUrl ? <PlainHandoffLink nextUrl={nextUrl} label="Finish my check" /> : null}
      <LockedItemsList items={items} />
      <CallbackLine lead={CALLBACK_OFFER_LEAD} />
    </div>
  );
}

function WaitingView() {
  // The calm we-owe-you card: NO CTA anywhere — nothing is needed from the
  // patient, and a control here would invent an obligation.
  return (
    <div>
      <HeroBadge tone="tint">
        <ClockGlyph size={26} />
      </HeroBadge>
      <Title>We&rsquo;re reviewing — nothing needed from you</Title>
      <Help>
        Your answers are with your clinician now. We&rsquo;ll email you as soon as your plan is ready — and your
        guides unlock with it.
      </Help>
      <CallbackLine lead="Questions in the meantime?" />
    </div>
  );
}

function UnlockedView({
  items,
  onDownload,
}: {
  items: UnlockItem[];
  onDownload: (contentId: string) => Promise<TileOutcome>;
}) {
  return (
    <div>
      <HeroBadge tone="solid">
        <TickGlyph size={26} />
      </HeroBadge>
      <Title>You&rsquo;re all set — your guides are unlocked</Title>
      <Help>
        Your plan is confirmed and your care team will keep these up to date. Download them whenever you like — this
        link keeps working while your plan is active.
      </Help>
      <SectionEyebrow>Your guides</SectionEyebrow>
      <Card style={{ padding: 0 }}>
        {items.map((item, index) => (
          <DownloadRow key={item.contentId} item={item} last={index === items.length - 1} onDownload={onDownload} />
        ))}
        {items.length === 0 ? (
          // An unlocked link with nothing on it yet is a real state (the plan
          // unlocked before anything was sent) — say so, never a blank card.
          <p style={{ margin: 0, padding: '14px 16px', fontSize: '14px', color: 'var(--gps-muted)' }}>
            Nothing here yet — new guides your care team sends will appear on this page.
          </p>
        ) : null}
      </Card>
      <div
        style={{
          background: 'var(--gps-primary-light)',
          borderRadius: 'var(--gps-r-md)',
          padding: '14px 16px',
          margin: 'var(--gps-s-3) 0 0',
          fontSize: '13.5px',
          lineHeight: 1.5,
          color: 'var(--gps-green-text)',
        }}
      >
        New guides your team sends will appear here too — same link, nothing else to do.
      </div>
      <CallbackLine lead="Questions about your plan?" />
    </div>
  );
}

function LapsedView({ items }: { items: UnlockItem[] }) {
  // No board exists for `lapsed`; this is the locked framing with honest
  // end-of-plan copy. The wire supplies NO nextUrl for this state (a plan
  // that has ENDED has no lane to hand into — the server's contract), so the
  // onward routes are the page's OWN: a fresh check, and the callback line —
  // never a CTA built out of a null.
  return (
    <div>
      <HeroBadge tone="tint">
        <LockGlyph size={26} />
      </HeroBadge>
      <Title>Your plan has ended</Title>
      <Help>
        Your guides lock when a plan ends. Start a new check whenever you&rsquo;re ready — it only takes a couple of
        minutes — and they unlock again with your new plan.
      </Help>
      <Link to={JOURNEY_PATH} className={buttonClass('cta')} style={buttonStyle('cta')}>
        Start a new check
      </Link>
      <LockedItemsList items={items} />
      <CallbackLine lead={CALLBACK_OFFER_LEAD} />
    </div>
  );
}

function NoneView() {
  return (
    <div>
      <HeroBadge tone="muted">
        <LockGlyph size={26} muted />
      </HeroBadge>
      <Title>Nothing to unlock here</Title>
      <Help>
        This link works, but there&rsquo;s nothing waiting behind it right now. If you were expecting something, get
        in touch and we&rsquo;ll take a look.
      </Help>
      <CallbackLine lead="Want to ask us about it?" />
    </div>
  );
}

/** The UnlockExpired board: ONE calm page for every dead link — expired,
 * revoked, forged, purged and a transport fault all look identical here, on
 * purpose. The page never says which. */
function DeadLinkView() {
  return (
    <div>
      <HeroBadge tone="muted">
        <ClockGlyph size={26} muted />
      </HeroBadge>
      <Title>This link isn&rsquo;t active any more</Title>
      <Help>
        Links like this one stop working after a while, or once things have moved on. There&rsquo;s nothing wrong on
        your side — if you&rsquo;d like a fresh one, just ask and we&rsquo;ll send it over.
      </Help>
      <div style={{ display: 'grid', gap: 'var(--gps-s-2)' }}>
        <Link to={CALLBACK_PATH} className={buttonClass('cta')} style={buttonStyle('cta')}>
          Request a free call back
        </Link>
        <Link to={JOURNEY_PATH} className={buttonClass('ghost')} style={buttonStyle('ghost')}>
          Start a new check
        </Link>
      </div>
    </div>
  );
}

/* ── Shared furniture ───────────────────────────────────────────────────── */

/**
 * The ONE navigation out of this page — `location.assign(nextUrl)` into the
 * live journey. A named seam rather than an inline call ONLY because jsdom's
 * `location.assign` is non-configurable AND non-writable, so no spec could
 * otherwise observe which URL the CTA hands over. Production behaviour is
 * exactly `window.location.assign`.
 */
export const unlockHandoff = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

/**
 * The GRANT-shaped unlock CTA — a BUTTON calling `location.assign(nextUrl)`,
 * never an `<a href>`: the URL's fragment carries a live short-TTL grant, and
 * an anchor at rest parks it in the copy-link menu and the hover status bar.
 * Only `needs_plan` uses it — the one state whose nextUrl is grant-shaped.
 */
function HandoffCta({ nextUrl, label }: { nextUrl: string; label: string }) {
  return (
    <Button type="button" variant="cta" onClick={() => unlockHandoff.assign(nextUrl)}>
      {label}
    </Button>
  );
}

/**
 * The PLAIN hand-off — a REAL anchor, because this URL carries NO grant:
 * `needs_questionnaire`'s nextUrl is the plain restart lane
 * (`/weightloss?src=unlock` — provenance, not a credential), safe at rest in
 * a copy-link menu and the hover status bar. A full-document navigation on
 * purpose: the server mints an absolute URL into the live journey.
 */
function PlainHandoffLink({ nextUrl, label }: { nextUrl: string; label: string }) {
  return (
    <a href={nextUrl} className={buttonClass('cta')} style={buttonStyle('cta')}>
      {label}
    </a>
  );
}

/** The locked "Waiting for you" list — titles visible, nothing downloadable. */
function LockedItemsList({ items }: { items: UnlockItem[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 'var(--gps-s-4)' }}>
      <SectionEyebrow>Waiting for you</SectionEyebrow>
      <Card style={{ padding: 0 }}>
        {items.map((item, index) => (
          <div
            key={item.contentId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              borderBottom: index === items.length - 1 ? 'none' : '1px solid var(--gps-border)',
            }}
          >
            <LockGlyph size={18} muted />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600, fontSize: '14.5px', color: 'var(--gps-text)' }}>
                {item.title}
              </span>
              <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--gps-muted)' }}>
                PDF · from your care team
              </span>
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}

/** One unlocked guide row: title + its own Download with its own outcome note. */
function DownloadRow({
  item,
  last,
  onDownload,
}: {
  item: UnlockItem;
  last: boolean;
  onDownload: (contentId: string) => Promise<TileOutcome>;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function press() {
    if (busy) return;
    setBusy(true);
    setNote(null);
    const outcome = await onDownload(item.contentId);
    // 'superseded' sets nothing: the page is re-resolving and this row is
    // about to be replaced by whatever the server now says.
    if (outcome === 'unconfirmed') setNote(UNLOCK_UNCONFIRMED_MESSAGE);
    else if (outcome === 'failed') setNote(UNLOCK_DOWNLOAD_FAILED_MESSAGE);
    setBusy(false);
  }

  return (
    <div style={{ padding: '14px 16px', borderBottom: last ? 'none' : '1px solid var(--gps-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <DocGlyph size={20} />
        <span style={{ flexGrow: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: '14.5px', color: 'var(--gps-text)' }}>
            {item.title}
          </span>
          <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--gps-muted)' }}>
            PDF · from your care team
          </span>
        </span>
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => void press()}
          style={{ width: 'auto' }}
          aria-label={`Download ${item.title}`}
        >
          {busy ? 'Opening…' : 'Download'}
        </Button>
      </div>
      {note ? (
        <p role="status" style={{ margin: '8px 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
          {note}
        </p>
      ) : null}
    </div>
  );
}

/** Small uppercase section label — `text-transform`, the DOM text stays
 * sentence case (a screen reader handed "WAITING FOR YOU" may spell it). */
function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'var(--gps-muted)',
        paddingBottom: '10px',
      }}
    >
      {children}
    </div>
  );
}

/** The in-app route to a human — the same callback lane the journey offers. */
function CallbackLine({ lead }: { lead: string }) {
  return (
    <p style={{ margin: 'var(--gps-s-4) 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>
      {lead}{' '}
      <Link to={CALLBACK_PATH} style={{ fontWeight: 600, color: 'var(--gps-link)' }}>
        Request a free call back
      </Link>
      .
    </p>
  );
}

function HeroBadge({ tone, children }: { tone: 'tint' | 'solid' | 'muted'; children: ReactNode }) {
  const background =
    tone === 'solid' ? 'var(--gps-primary)' : tone === 'tint' ? 'var(--gps-primary-light)' : 'var(--gps-surface-2)';
  return (
    <div
      aria-hidden="true"
      style={{
        width: '58px',
        height: '58px',
        borderRadius: '50%',
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 'var(--gps-s-3)',
      }}
    >
      {children}
    </div>
  );
}

/* ── Glyphs — hand-rolled inline SVG (the CSP-locked house pattern), all
 *    decorative: aria-hidden, focusable=false, named text beside them. ── */

function LockGlyph({ size, muted = false }: { size: number; muted?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={muted ? 'var(--gps-muted)' : 'var(--gps-primary)'}
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0" />
    </svg>
  );
}

function TickGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--gps-on-accent)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 12l5 5 9-10" />
    </svg>
  );
}

function TickBoxGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--gps-primary)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9 11l3 3 8-8" />
      <path d="M20 12v5a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h9" />
    </svg>
  );
}

function ClockGlyph({ size, muted = false }: { size: number; muted?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={muted ? 'var(--gps-muted)' : 'var(--gps-primary)'}
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

function DocGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--gps-primary)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}
